import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fonts, colors, radii } from "../designTokens";
import { getConsent, setConsent, hasGivenConsent, migrateOldConsent } from "../consent";
import { localizedHref } from "../utils/localizedNav";

export default function CookieConsentBanner({ onAnalyticsAccepted }) {
  const { t, ready } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    migrateOldConsent();
    if (!hasGivenConsent()) {
      setVisible(true);
    } else if (getConsent()?.analytics) {
      onAnalyticsAccepted?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Erst anzeigen, wenn die Übersetzungen geladen sind: sonst wechselt der Text nach dem
  // Erscheinen und die Banner-Höhe springt (war ~0,09 Layout-Shift auf Mobil).
  if (!visible || !ready) return null;

  function acceptAll() {
    setConsent({ analytics: true, marketing: true });
    setVisible(false);
    onAnalyticsAccepted?.();
  }

  function essentialOnly() {
    setConsent({ analytics: false, marketing: false });
    setVisible(false);
  }

  function saveSelection() {
    setConsent({ analytics, marketing });
    setVisible(false);
    if (analytics) onAnalyticsAccepted?.();
  }

  return (
    <div
      role="dialog"
      aria-label={t("cookie.manager.title")}
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: "var(--aof-card-bg, #ffffff)",
        borderTop: "1px solid var(--aof-card-border, #e8e8e8)",
        boxShadow: "0 -4px 24px rgba(0,0,0,0.13)",
        fontFamily: fonts.body,
      }}
    >
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "14px 18px" }}>
        {/* Main row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>🍪</span>
          <div style={{ flex: 1, minWidth: 180 }}>
            <strong style={{ color: "var(--aof-text-dark, #222)", fontSize: 13.5, fontFamily: fonts.body }}>
              {t("cookie.manager.title")}
            </strong>{" "}
            <span style={{ color: "var(--aof-text-body, #555)", fontSize: 13 }}>
              {t("cookie.manager.intro")}{" "}
              <a
                href={localizedHref("/privacy")}
                style={{ color: colors.orange, textDecoration: "underline" }}
              >
                {t("cookie.manager.privacyLink")}
              </a>
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
            <button
              onClick={() => setExpanded(e => !e)}
              style={{
                background: "none",
                border: "1px solid var(--aof-card-border, #e0e0e0)",
                borderRadius: radii.md,
                padding: "7px 12px",
                cursor: "pointer",
                fontSize: 13,
                color: "var(--aof-text-dark, #222)",
                fontFamily: fonts.body,
                whiteSpace: "nowrap",
              }}
            >
              {t("cookie.manager.details")} {expanded ? "▲" : "▼"}
            </button>
            <button
              onClick={essentialOnly}
              style={{
                background: "var(--aof-input-bg, #f0f0f0)",
                border: "1px solid var(--aof-card-border, #e0e0e0)",
                borderRadius: radii.md,
                padding: "7px 14px",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--aof-text-dark, #444)",
                fontFamily: fonts.body,
                whiteSpace: "nowrap",
              }}
            >
              {t("cookie.essential")}
            </button>
            <button
              onClick={acceptAll}
              style={{
                background: colors.green,
                border: "none",
                borderRadius: radii.md,
                padding: "7px 18px",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                color: "#fff",
                fontFamily: fonts.body,
                whiteSpace: "nowrap",
              }}
            >
              {t("cookie.accept")}
            </button>
          </div>
        </div>

        {/* Expanded detail panel */}
        {expanded && (
          <div
            style={{
              marginTop: 14,
              borderTop: "1px solid var(--aof-card-border, #e8e8e8)",
              paddingTop: 14,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <ConsentRow
              label={t("cookie.manager.necessary")}
              desc={t("cookie.manager.necessaryDesc")}
              alwaysOn
              alwaysOnLabel={t("cookie.manager.alwaysOn")}
            />
            <ConsentRow
              label={t("cookie.manager.analytics")}
              desc={t("cookie.manager.analyticsDesc")}
              checked={analytics}
              onChange={setAnalytics}
            />
            <ConsentRow
              label={t("cookie.manager.marketing")}
              desc={t("cookie.manager.marketingDesc")}
              checked={marketing}
              onChange={setMarketing}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <button
                onClick={saveSelection}
                style={{
                  background: colors.green,
                  border: "none",
                  borderRadius: radii.md,
                  padding: "8px 22px",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#fff",
                  fontFamily: fonts.body,
                }}
              >
                {t("cookie.manager.saveSelection")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentRow({ label, desc, alwaysOn, alwaysOnLabel, checked, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontWeight: 700,
            color: "var(--aof-text-dark, #222)",
            fontSize: 13,
            fontFamily: fonts.body,
          }}
        >
          {label}
        </div>
        <div
          style={{
            color: "var(--aof-text-muted, #888)",
            fontSize: 12,
            marginTop: 2,
            fontFamily: fonts.body,
          }}
        >
          {desc}
        </div>
      </div>
      {alwaysOn ? (
        <span
          style={{
            fontSize: 12,
            color: colors.green,
            fontWeight: 700,
            whiteSpace: "nowrap",
            fontFamily: fonts.body,
          }}
        >
          ✓ {alwaysOnLabel}
        </span>
      ) : (
        <ToggleSwitch checked={checked} onChange={onChange} />
      )}
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: checked ? colors.green : "#bbb",
        border: "none",
        cursor: "pointer",
        position: "relative",
        transition: "background 0.2s",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 22 : 3,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.2s",
          display: "block",
          pointerEvents: "none",
        }}
      />
    </button>
  );
}
